import { useI18n } from 'vue-i18n'
import { useConfirm, useToast, errorMessage } from '@general/ui-kit'

import { calendarApi } from './api'
import { useScopeChoice } from './scope'
import { touch } from './store'
import { atTime } from './lib/time.mjs'

// What can be done to an event from any of the screens that show one — the
// week view, the peek, the event page — written once, so «cancel» asks the
// same question and says the same thing wherever it is pressed.

const isRepeated = event => Boolean(event?.series || event?.recurrence || event?.seriesId || event?.occurrence)
const occurrenceOf = event => event?.occurrence ?? event?.recurrenceDate ?? null

export function useEventActions() {
  const { t } = useI18n()
  const toast = useToast()
  const { confirm } = useConfirm()
  const { ask } = useScopeChoice()

  async function run(work, done) {
    try {
      const result = await work()
      if (done) toast.success(done)
      touch()
      return result ?? true
    } catch (error) {
      toast.error(errorMessage(error, t))
      return false
    }
  }

  /** Cancels it: it stays in the history and whoever was invited is told. */
  async function cancelEvent(event) {
    let scope = 'all'
    if (isRepeated(event)) {
      scope = await ask({ action: 'cancel' })
      if (!scope) return false
    } else {
      const guests = (event.attendees?.length ?? 0) + (event.externals?.length ?? 0)
      const yes = await confirm({
        title: t('calendar.confirm.cancelTitle'),
        message: guests ? t('calendar.confirm.cancelGuests') : t('calendar.confirm.cancelText'),
        confirmLabel: t('calendar.actions.cancelEvent'),
        danger: true,
      })
      if (!yes) return false
    }
    return run(() => calendarApi.cancel(event.id, { scope, occurrence: occurrenceOf(event) }), t('calendar.toast.cancelled'))
  }

  /** Deletes it for good — the whole series. Only its owner. */
  async function removeEvent(event) {
    const yes = await confirm({
      title: t('calendar.confirm.deleteTitle'),
      message: isRepeated(event) ? t('calendar.confirm.deleteSeries') : t('calendar.confirm.deleteText'),
      confirmLabel: t('calendar.actions.delete'),
      danger: true,
    })
    if (!yes) return false
    return run(() => calendarApi.remove(event.id), t('calendar.toast.deleted'))
  }

  /**
   * Moved by dragging: the new instants go as instants, so the event keeps the
   * zone it was written on — dragging a New York meeting on a Madrid screen
   * must not turn it into a Madrid meeting.
   */
  async function moveEvent(event, { date, start, end, zone }) {
    let scope = 'all'
    if (isRepeated(event)) {
      scope = await ask({ action: 'move' })
      if (!scope) return false
    }
    const body = {}
    if (start) body.startsAt = atTime(date, start, zone).toISOString()
    body.endsAt = atTime(date, end, zone).toISOString()
    return run(() => calendarApi.update(event.id, body, { scope, occurrence: occurrenceOf(event) }), t('calendar.toast.moved'))
  }

  async function respond(event, response) {
    return run(() => calendarApi.respond(event.id, { response }), t(`calendar.toast.responded.${response}`))
  }

  return { cancelEvent, removeEvent, moveEvent, respond, isRepeated }
}
